import { supabase } from '@/lib/supabase';

export interface CompleteDeletionResult {
  success: boolean;
  message: string;
  error?: string;
  deletedUser?: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
}

/**
 * Completely deletes a user from all tables including authentication
 * This function calls a Supabase Edge Function that has admin privileges
 */
export async function removeUserCompletely(userId: string): Promise<CompleteDeletionResult> {
  try {
    console.log('Starting complete user deletion for:', userId);
    
    // Get the current user's session token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    // Call the Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('delete-user', {
      body: { userId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(`Failed to delete user: ${error.message}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete user');
    }

    console.log('Complete deletion successful:', data);
    return data;

  } catch (error) {
    console.error('Complete deletion error:', error);
    return {
      success: false,
      error: (error as Error).message,
      message: 'Failed to completely delete user'
    };
  }
}

/**
 * Alternative method using the database function (marks as deleted)
 * This doesn't actually delete from auth.users but marks the user as deleted
 */
export async function markUserAsDeleted(userId: string): Promise<CompleteDeletionResult> {
  try {
    console.log('Marking user as deleted:', userId);
    
    const { data, error } = await supabase.rpc('delete_user_completely', {
      user_id: userId
    });

    if (error) {
      console.error('Database function error:', error);
      throw new Error(`Failed to mark user as deleted: ${error.message}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to mark user as deleted');
    }

    console.log('User marked as deleted:', data);
    return {
      success: true,
      message: 'User marked as deleted. Auth deletion requires server-side action.',
      deletedUser: {
        id: userId,
        email: 'Unknown',
        name: 'Unknown',
        role: 'Unknown'
      }
    };

  } catch (error) {
    console.error('Mark as deleted error:', error);
    return {
      success: false,
      error: (error as Error).message,
      message: 'Failed to mark user as deleted'
    };
  }
}
